import type { ConversationHistoryItem, LocaleStrings } from '../types';
import { createElement } from '../utils/dom';

export interface ActiveConversationInfo {
  ticketId: string;
  lastMessageBody?: string;
  lastMessageAt?: string;
  agentName?: string;
  unreadCount?: number;
}

export interface ConversationListCallbacks {
  onClose: () => void;
  onNewConversation: () => void;
  onSelectActive: () => void;
  onSelectPast: (ticketId: string, title: string) => void;
  onLoadConversations: (cursor?: string) => Promise<{
    conversations: ConversationHistoryItem[];
    hasMore: boolean;
    cursor?: string;
  }>;
}

/**
 * Conversation history list screen
 */
export class ConversationList {
  private element: HTMLElement | null = null;
  private listContainer: HTMLElement | null = null;
  private titleElement: HTMLElement | null = null;
  private callbacks: ConversationListCallbacks;
  private strings: LocaleStrings;
  private activeInfo: ActiveConversationInfo | null = null;
  private cursor: string | undefined = undefined;
  private hasMore = false;
  private loadMoreButton: HTMLElement | null = null;
  private isLoadingMore = false;

  constructor(callbacks: ConversationListCallbacks, strings: LocaleStrings) {
    this.callbacks = callbacks;
    this.strings = strings;
  }

  setStrings(strings: LocaleStrings): void {
    this.strings = strings;
    if (this.titleElement) {
      this.titleElement.textContent = strings.conversationsTitle;
    }
  }

  setActiveConversation(info: ActiveConversationInfo | null): void {
    this.activeInfo = info;
  }

  render(): HTMLElement {
    if (this.element) return this.element;

    this.element = createElement('div', {
      className: 'cevro-conversation-list',
    });

    // Header
    const header = createElement('div', {
      className: 'cevro-conversation-list__header',
    });

    this.titleElement = createElement('h2', {
      className: 'cevro-conversation-list__title',
    }, [this.strings.conversationsTitle]);

    const closeButton = createElement('button', {
      className: 'cevro-conversation-list__close',
      'aria-label': 'Close',
    });
    closeButton.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <line x1="18" y1="6" x2="6" y2="18"/>
        <line x1="6" y1="6" x2="18" y2="18"/>
      </svg>
    `;
    closeButton.addEventListener('click', () => this.callbacks.onClose());

    header.appendChild(this.titleElement);
    header.appendChild(closeButton);
    this.element.appendChild(header);

    // Scrollable list container
    this.listContainer = createElement('div', {
      className: 'cevro-conversation-list__items',
    });
    this.element.appendChild(this.listContainer);

    // New Conversation button
    const newConvButton = createElement('button', {
      className: 'cevro-conversation-list__new-conversation',
    }, [this.strings.newConversationButton]);
    newConvButton.addEventListener('click', () => this.callbacks.onNewConversation());

    // Footer
    const footer = createElement('div', {
      className: 'cevro-conversation-list__footer',
    });
    footer.innerHTML = `${this.strings.poweredBy.replace('Cevro AI', '<a href="https://cevro.ai" target="_blank" rel="noopener">Cevro AI</a>')}`;

    // Action panel wrapping button + powered-by (per design)
    const actionPanel = createElement('div', { className: 'cevro-action-panel' });
    actionPanel.appendChild(newConvButton);
    actionPanel.appendChild(footer);
    this.element.appendChild(actionPanel);

    return this.element;
  }

  /**
   * Load and display conversations
   */
  async load(): Promise<void> {
    if (!this.listContainer) return;

    // Reset pagination state
    this.cursor = undefined;
    this.hasMore = false;
    this.loadMoreButton = null;

    // Show loading
    this.listContainer.innerHTML = '';
    const loading = createElement('div', {
      className: 'cevro-conversation-list__loading',
    }, [this.strings.loadingConversations]);
    this.listContainer.appendChild(loading);

    try {
      const result = await this.callbacks.onLoadConversations();

      this.listContainer.innerHTML = '';
      this.cursor = result.cursor;
      this.hasMore = result.hasMore;

      // Active conversation card (if exists) — shows last message preview
      if (this.activeInfo) {
        const activeCard = this.createActiveCard();
        this.listContainer.appendChild(activeCard);
      }

      // Past conversations
      if (result.conversations.length === 0 && !this.activeInfo) {
        this.showEmptyState();
        return;
      }

      for (const conv of result.conversations) {
        // Skip if this is the active ticket
        if (conv.ticketId === this.activeInfo?.ticketId) continue;
        const card = this.createConversationCard(conv);
        this.listContainer.appendChild(card);
      }

      this.renderLoadMoreButton();
    } catch {
      this.listContainer.innerHTML = '';
      if (this.activeInfo) {
        const activeCard = this.createActiveCard();
        this.listContainer.appendChild(activeCard);
      }
      this.showEmptyState();
    }
  }

  private async loadMore(): Promise<void> {
    if (!this.listContainer || !this.hasMore || this.isLoadingMore) return;

    this.isLoadingMore = true;
    if (this.loadMoreButton) {
      this.loadMoreButton.textContent = this.strings.loadingConversations;
      (this.loadMoreButton as HTMLButtonElement).disabled = true;
    }

    try {
      const result = await this.callbacks.onLoadConversations(this.cursor);
      this.cursor = result.cursor;
      this.hasMore = result.hasMore;

      // Remove old load-more button before appending new cards
      this.loadMoreButton?.remove();
      this.loadMoreButton = null;

      for (const conv of result.conversations) {
        if (conv.ticketId === this.activeInfo?.ticketId) continue;
        const card = this.createConversationCard(conv);
        this.listContainer!.appendChild(card);
      }

      this.renderLoadMoreButton();
    } catch {
      // Restore button on error
      if (this.loadMoreButton) {
        this.loadMoreButton.textContent = this.strings.loadMoreConversations;
        (this.loadMoreButton as HTMLButtonElement).disabled = false;
      }
    } finally {
      this.isLoadingMore = false;
    }
  }

  private renderLoadMoreButton(): void {
    if (!this.hasMore || !this.listContainer) return;

    this.loadMoreButton = createElement('button', {
      className: 'cevro-conversation-list__load-more',
    }, [this.strings.loadMoreConversations]);
    this.loadMoreButton.addEventListener('click', () => this.loadMore());
    this.listContainer.appendChild(this.loadMoreButton);
  }

  private createActiveCard(): HTMLElement {
    const unread = this.activeInfo?.unreadCount ?? 0;
    const card = createElement('div', {
      className: `cevro-conversation-card cevro-conversation-card--active${unread > 0 ? ' cevro-conversation-card--unread' : ''}`,
    });

    const content = createElement('div', {
      className: 'cevro-conversation-card__content',
    });

    const titleRow = createElement('div', {
      className: 'cevro-conversation-card__title-row',
    });

    const agentName = createElement('span', {
      className: 'cevro-conversation-card__title',
    }, [this.activeInfo?.agentName || 'Support']);

    const date = createElement('span', {
      className: 'cevro-conversation-card__date',
    }, [this.activeInfo?.lastMessageAt ? this.formatDate(this.activeInfo.lastMessageAt) : '']);

    titleRow.appendChild(agentName);
    titleRow.appendChild(date);

    const previewRow = createElement('div', {
      className: 'cevro-conversation-card__preview-row',
    });

    const preview = createElement('div', {
      className: 'cevro-conversation-card__preview',
    }, [this.activeInfo?.lastMessageBody || this.strings.inputPlaceholder]);
    previewRow.appendChild(preview);

    if (unread > 0) {
      const unreadBadge = createElement('span', {
        className: 'cevro-conversation-card__unread',
      }, [unread > 99 ? '99+' : String(unread)]);
      previewRow.appendChild(unreadBadge);
    }

    content.appendChild(titleRow);
    content.appendChild(previewRow);
    card.appendChild(content);

    card.addEventListener('click', () => this.callbacks.onSelectActive());
    return card;
  }

  private createConversationCard(conv: ConversationHistoryItem): HTMLElement {
    const card = createElement('div', {
      className: 'cevro-conversation-card',
    });

    const content = createElement('div', {
      className: 'cevro-conversation-card__content',
    });

    const titleRow = createElement('div', {
      className: 'cevro-conversation-card__title-row',
    });

    const titleText = createElement('span', {
      className: 'cevro-conversation-card__title',
    }, [conv.title || this.strings.defaultConversationTitle]);

    const date = createElement('span', {
      className: 'cevro-conversation-card__date',
    }, [this.formatDate(conv.lastMessageAt || conv.createdAt)]);

    titleRow.appendChild(titleText);
    titleRow.appendChild(date);

    const preview = createElement('div', {
      className: 'cevro-conversation-card__preview',
    }, [conv.lastMessageBody || `${conv.messageCount} ${this.strings.messagesCount}`]);

    content.appendChild(titleRow);
    content.appendChild(preview);
    card.appendChild(content);

    card.addEventListener('click', () => {
      this.callbacks.onSelectPast(conv.ticketId, conv.title);
    });

    return card;
  }

  private showEmptyState(): void {
    if (!this.listContainer) return;

    const empty = createElement('div', {
      className: 'cevro-conversation-list__empty',
    });

    const emptyTitle = createElement('p', {
      className: 'cevro-conversation-list__empty-title',
    }, [this.strings.noConversations]);

    const emptySubtitle = createElement('p', {
      className: 'cevro-conversation-list__empty-subtitle',
    }, [this.strings.noConversationsSubtitle]);

    empty.appendChild(emptyTitle);
    empty.appendChild(emptySubtitle);
    this.listContainer.appendChild(empty);
  }

  private formatDate(dateStr: string): string {
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      } else if (diffDays === 1) {
        return this.strings.yesterday;
      } else if (diffDays < 7) {
        return date.toLocaleDateString([], { weekday: 'short' });
      } else {
        return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
      }
    } catch {
      return '';
    }
  }

  show(): void {
    if (this.element) {
      this.element.style.display = 'flex';
    }
  }

  hide(): void {
    if (this.element) {
      this.element.style.display = 'none';
    }
  }

  destroy(): void {
    if (this.element && this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
    this.element = null;
    this.listContainer = null;
  }
}
